<template>
  <div class="bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 rounded-3xl py-4 px-3">
    <div class="w-full max-w-md mx-auto space-y-4" dir="rtl">
      <div
          class="rounded-3xl bg-white/5 border border-white/10 backdrop-blur-2xl shadow-2xl shadow-black/40 px-4 py-3 flex items-center justify-between"
      >
        <div class="flex items-center gap-4 pt-2">
          <div class="relative">
            <div class="w-14 h-14 rounded-2xl bg-slate-900/80 backdrop-blur-xl border border-amber-400/30
                   flex items-center justify-center shadow-2xl shadow-amber-500/20 overflow-hidden">
              <img
                  v-if="user.profile_picture"
                  :src="img_source(user.profile_picture)"
                  :alt="user.username"
                  class="w-full h-full object-cover"
              >
              <div v-else class="w-full h-full bg-gradient-to-br from-amber-500 to-pink-600 flex items-center justify-center">
                <span class="text-lg font-black text-white">{{ user.username.charAt(0) || 'U' }}</span>
              </div>
            </div>

            <!-- Online Badge -->
            <div class="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full border-2 border-slate-900
                   flex items-center justify-center shadow-lg shadow-emerald-500/50">
              <div class="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
            </div>

            <!-- Glow Effect -->
            <div class="absolute -inset-2 rounded-2xl bg-amber-400/10 blur-md opacity-60"></div>
          </div>

          <div class="text-right">
            <h1 class="text-xl font-bold text-slate-50 drop-shadow-lg flex items-center gap-2">
              مدیریت
              <span class="text-amber-300">کیف پول</span>
            </h1>
            <p class="text-sm text-slate-300/80 mt-1 font-light">
              {{ user.username || 'کاربر' }} - ادمین  پشتبانی
              <span class="text-xs px-2 py-1 bg-emerald-500/20 text-emerald-300 rounded-lg border border-emerald-400/30">
                آنلاین
              </span>

            </p>
          </div>
        </div>
      </div>

      <!-- Search Card -->
      <div
          class="rounded-3xl bg-white/5 border border-white/10 backdrop-blur-2xl shadow-2xl shadow-black/40 p-4 space-y-3"
      >
        <div class="flex items-center justify-between mb-1">
          <span class="text-sm font-medium text-slate-100">جستجوی سفارش‌ها بر اساس نام کاربری</span>
          <span class="text-[10px] text-slate-400">username / نام کاربر</span>
        </div>

        <!-- Username Input + Button -->
        <div class="flex items-center gap-2">
          <div class="flex-1 relative">
            <input
                v-model.trim="filters.username"
                type="text"
                class="w-full rounded-2xl bg-slate-950/60 border border-white/10 px-3 py-2.5 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-400/60 focus:border-emerald-300/60 transition"
                placeholder="مثلاً: exuni_user"
                @keyup.enter="fetchOrders"
            />
            <div class="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">
              🔎
            </div>
          </div>
          <button
              type="button"
              @click="fetchOrders"
              :disabled="loadingOrders || !filters.username"
              class="shrink-0 inline-flex items-center justify-center rounded-2xl px-3 py-2 text-xs font-medium
                   bg-emerald-400/90 text-slate-900 hover:bg-emerald-300
                   disabled:opacity-40 disabled:cursor-not-allowed
                   shadow-lg shadow-emerald-500/30 transition"
          >
            <span v-if="!loadingOrders">جستجو</span>
            <span v-else class="flex items-center gap-1">
              <svg class="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3.5-3.5L12 0v4a8 8 0 00-8 8h4z"></path>
              </svg>
              در حال جستجو...
            </span>
          </button>
        </div>

        <!-- Info text -->
        <p class="text-[11px] leading-relaxed text-slate-400">
          ابتدا نام کاربری یا نام مشتری را وارد کنید، سپس از بین سفارش‌های پیدا شده، یکی را برای شارژ کیف پول انتخاب کنید.
        </p>

        <!-- Error زیر سرچ (در کنار popup) -->
        <p v-if="errorOrders" class="text-xs text-rose-400 mt-1">
          {{ errorOrders }}
        </p>
      </div>

      <!-- Orders List -->
      <div
          v-if="orders.length"
          class="rounded-3xl pb-3 bg-white/5 border border-white/10 backdrop-blur-2xl shadow-2xl shadow-black/40 p-3 space-y-2 max-h-[45vh] overflow-y-auto"
      >
        <div class="flex items-center justify-between mb-1 px-1">
          <span class="text-xs font-medium text-slate-100">سفارش‌های پیدا شده</span>
          <span class="text-[10px] text-slate-400">{{ orders.length }} مورد</span>
        </div>

        <button
            v-for="order in orders"
            :key="order.id"
            type="button"
            @click="selectOrder(order)"
            class="w-full text-right rounded-2xl border transition px-3 py-2.5 mb-1.5
                 flex flex-col gap-1.5
                 bg-slate-950/60 border-white/10 hover:border-emerald-300/60 hover:bg-slate-900/80
                 focus:outline-none"
            :class="{
            'ring-2 ring-emerald-400/60 border-emerald-300/60': selectedOrder && selectedOrder.id === order.id
          }"
        >
          <div class="flex items-center justify-between gap-2">
            <div class="flex flex-col">
              <span class="text-xs font-semibold text-slate-100">
                {{ order.customer_name || 'بدون نام' }}
              </span>
              <span class="text-[11px] text-slate-400">
                موبایل : {{ order.customer_username }}
              </span>
            </div>
            <span
                class="text-[10px] px-2 py-1 rounded-xl bg-emerald-400/10 border border-emerald-300/40 text-emerald-200"
            >
              شناسه :
            {{ order.id }}
            </span>
          </div>
          <div class="flex items-center justify-between text-[11px] text-slate-400">
            <span>مبلغ سفارش: <span class="text-emerald-300 font-medium">{{ formatPrice(order.total_price) }}</span></span>
            <span>ثبت در : <span class="text-slate-200">{{ order.date_time}}</span></span>
          </div>
        </button>
      </div>

      <div
          v-else-if="!loadingOrders && filters.username"
          class="rounded-3xl bg-white/5 border border-dashed border-white/15 backdrop-blur-2xl shadow-inner shadow-black/40 p-4 text-center"
      >
        <p class="text-xs text-slate-300 mb-1">سفارشی برای این کاربر پیدا نشد.</p>
        <p class="text-[11px] text-slate-500">username را چک کنید یا نام دیگری را امتحان کنید.</p>
      </div>

      <div
          v-if="!selectedOrder"
          class="rounded-3xl bg-white/5 border border-dashed border-white/15 backdrop-blur-2xl shadow-inner shadow-black/40 p-4 text-center text-[11px] text-slate-400"
      >
        برای فعال شدن بخش شارژ کیف پول، یکی از سفارش‌های بالا را انتخاب کنید.
      </div>

      <div
          v-else
          class="rounded-3xl bg-white/5 border border-white/10 backdrop-blur-2xl shadow-2xl shadow-black/40 p-4 space-y-3"
      >
        <div class="flex items-center justify-between mb-1">
          <span class="text-sm font-medium text-slate-100">شارژ کیف پول کاربر</span>
          <span class="text-[10px] text-slate-400">
            سفارش انتخاب شده: {{ selectedOrder.exuni_tracking_code }}
          </span>
        </div>

        <div
            class="rounded-2xl bg-slate-950/60 border border-emerald-300/30 px-3 py-2.5 text-[11px] text-slate-200 space-y-1"
        >
          <div class="flex items-center justify-between">
            <span class="font-medium">{{ selectedOrder.customer_name || 'بدون نام' }}</span>
            <span class="text-emerald-300 text-[10px]">{{ selectedOrder.exuni_tracking_code }}</span>
          </div>
          <div class="flex items-center justify-between text-slate-400">
            <span>مبلغ سفارش: <span class="text-emerald-300">{{ formatPrice(selectedOrder.total_price) }}</span></span>
            <span>username: <span class="text-slate-200">@{{ selectedOrder.customer_username }}</span></span>
          </div>
        </div>

        <div class="space-y-1.5 mt-1">
          <label class="text-xs text-slate-300">مبلغ شارژ (تومان)</label>
          <div class="relative">
            <input
                :value="form.amount"
                @input="handleAmountInput"
                inputmode="decimal"
                class="w-full rounded-2xl bg-slate-950/60 border border-white/10 px-3 py-2.5 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-400/60 focus:border-emerald-300/60 transition"
                placeholder="مثلاً: 500,000"
            />
            <div class="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-500">
              تومان
            </div>
          </div>
        </div>

        <div class="space-y-1.5">
          <label class="text-xs text-slate-300">توضیحات (اختیاری)</label>
          <textarea
              v-model.trim="form.description"
              rows="2"
              class="w-full rounded-2xl bg-slate-950/60 border border-white/10 px-3 py-2.5 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-400/60 focus:border-emerald-300/60 transition resize-none"
              placeholder="اگر خالی بگذارید، متن پیش‌فرض «بابت سفارش ... توسط ...» خودکار تنظیم می‌شود."
          ></textarea>
        </div>

        <p v-if="errorTopUp" class="text-xs text-rose-400">
          {{ errorTopUp }}
        </p>
        <div
            v-if="successTopUp"
            class="text-[11px] text-emerald-300 bg-emerald-400/10 border border-emerald-300/40 rounded-2xl px-3 py-2 flex items-center gap-2"
        >
          <span>✅</span>
          <span>{{ successTopUp }}</span>
        </div>

        <button
            type="button"
            @click="submitTopUp"
            :disabled="!canSubmit || loadingTopUp"
            class="w-full mt-3 inline-flex items-center justify-center rounded-2xl px-3 py-2.5 text-sm font-semibold
                 bg-emerald-400 text-slate-950 hover:bg-emerald-300
                 disabled:opacity-40 disabled:cursor-not-allowed
                 shadow-xl shadow-emerald-500/40 transition"
        >
          <span v-if="!loadingTopUp">شارژ کیف پول</span>
          <span v-else class="flex items-center gap-2">
            <svg class="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3.5-3.5L12 0v4a8 8 0 00-8 8h4z"></path>
            </svg>
            در حال ثبت شارژ...
          </span>
        </button>

        <p class="text-[10px] text-slate-500 text-center mt-1">
          اگر توضیحی وارد نکنید، متن پیش‌فرض شامل کد سفارش و نام ادمین به‌صورت خودکار در گزارش کیف پول ذخیره می‌شود.
        </p>
      </div>
    </div>

    <transition name="fade">
      <div
          v-if="popupError"
          class="fixed inset-x-0 top-3 flex justify-center px-3 z-50"
      >
        <div
            class="max-w-sm w-full rounded-2xl bg-rose-500/95 text-white text-xs px-3 py-2 shadow-lg shadow-rose-900/40 flex items-start gap-2"
        >
          <span class="mt-[2px]">⚠️</span>
          <span class="flex-1 leading-relaxed">{{ popupError }}</span>
          <button
              type="button"
              class="text-[10px] opacity-80 hover:opacity-100"
              @click="popupError = null"
          >
            بستن
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'WalletTopUpFromOrder',
  data () {
    return {
      filters: {
        username: ''
      },
      orders: [],
      selectedOrder: null,
      loadingOrders: false,
      errorOrders: null,

      form: {
        amount: '',
        description: ''
      },
      loadingTopUp: false,
      errorTopUp: null,
      successTopUp: null,

      popupError: null,
      popupTimeoutId: null
    }
  },
  computed: {
    canSubmit () {
      const clean = this.normalizeAmountForSend(this.form.amount || '')
      const amount = parseFloat(clean || 0)
      return this.selectedOrder && amount > 0
    }
  },
  methods: {
    handleAmountInput (e) {
      let raw = e.target.value || ''

      raw = raw.replace(/,/g, '').replace(/[^\d.]/g, '')

      if (!raw) {
        this.form.amount = ''
        return
      }

      const parts = raw.split('.')
      let intPart = parts[0]
      let decPart = parts[1] ? parts[1].slice(0, 2) : ''

      intPart = intPart.replace(/^0+(?=\d)/, '')

      intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

      this.form.amount = decPart ? `${intPart}.${decPart}` : intPart
    },

    normalizeAmountForSend (val) {
      if (!val) return ''
      return val.toString().replace(/,/g, '')
    },

    showErrorPopup (msg) {
      this.popupError = msg
      if (this.popupTimeoutId) {
        clearTimeout(this.popupTimeoutId)
      }
      this.popupTimeoutId = setTimeout(() => {
        this.popupError = null
        this.popupTimeoutId = null
      }, 3000)
    },

    normalizeErrorMessage (err, fallback) {
      if (Array.isArray(err) && err.length) {
        const first = err[0]
        if (first && first.messages && first.messages.length) {
          return first.messages[0]
        }
      }

      if (err && err.detail) {
        return err.detail
      }

      if (err && typeof err === 'object') {
        const keys = Object.keys(err)
        if (keys.length) {
          const k = keys[0]
          const v = err[k]
          if (Array.isArray(v) && v.length) {
            return v[0]
          }
        }
      }

      return fallback || 'خطای نامشخصی رخ داد.'
    },

    fetchOrders () {
      if (!this.filters.username) return

      this.loadingOrders = true
      this.errorOrders = null
      this.orders = []
      this.selectedOrder = null

      this.request({
        url: this.endpoint('shop/shop-orders/by-username'),
        method: 'get',
        params: {
          username: this.filters.username
        },
        success: (data) => {
          this.orders = data.results || data
          this.loadingOrders = false
        },
        error: (err) => {
          const msg = this.normalizeErrorMessage(err, 'خطا در دریافت لیست سفارش‌ها.')
          this.errorOrders = msg
          this.showErrorPopup(msg)
          this.loadingOrders = false
        },
        finally: () => {
          this.loadingOrders = false
        }
      })
    },
    selectOrder (order) {
      this.selectedOrder = order
      this.successTopUp = null
      this.errorTopUp = null
    },
    reset () {
      this.filters.username = null
      this.orders = []
      this.form.amount = 0
      this.selectedOrder = null
    },
    submitTopUp () {
      if (!this.canSubmit) return

      this.loadingTopUp = true
      this.errorTopUp = null
      this.successTopUp = null
      const cleanAmount = this.normalizeAmountForSend(this.form.amount)
      this.request({
        url: this.endpoint('finance/wallet/top-up-from-order'),
        method: 'post',
        data: {
          order_id: this.selectedOrder.id,
          amount: cleanAmount,
          description: this.form.description && this.form.description.trim()
              ? this.form.description.trim()
              : null
        },
        success: (data) => {
          this.successTopUp = 'شارژ کیف پول با موفقیت ثبت شد ✅'
          this.loadingTopUp = false
        },
        error: (err) => {
          const msg = this.normalizeErrorMessage(err, 'خطا در ثبت شارژ کیف پول.')
          this.errorTopUp = msg
          this.showErrorPopup(msg)
          this.loadingTopUp = false
        },
        finally: () => {
          this.loadingTopUp = false
        }
      })
    },
    formatPrice (value) {
      if (value === null || value === undefined) return '0'
      const num = parseFloat(value)
      if (isNaN(num)) return value
      return num.toLocaleString('fa-IR')
    },
    statusLabel (status) {
      const map = {
        pe: 'در انتظار پرداخت',
        pa: 'جاری',
        pr: 'درحال بسته‌بندی',
        pc: 'بسته‌بندی شد',
        sh: 'ارسال شده',
        de: 'تحویل شده',
        re: 'مرجوعی',
        ex: 'منقضی شده',
        ca: 'لغو شده',
        ed: 'ویرایش شده'
      }
      return map[status] || status
    }
  },
  watch: {
    successTopUp (val) {
      if (val) {
        setTimeout(() => {
          this.successTopUp = null
          this.reset()
        }, 3000)
      }
    },
    errorTopUp (val) {
      if (val) {
        setTimeout(() => {
          this.errorTopUp = null
        }, 5000)
      }
    }
  }

}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
